import { memo, useEffect } from 'react';
import { Controller } from 'react-hook-form';
import {
  Button,
  Modal,
  ModalFooter,
  SelectMenu,
  TextInput,
  TextArea,
} from '@spotoninc/nexus-react';
import { useImprovedTaskForm } from './useImprovedTaskForm';
import DateInput from '../../../common/DateInput';
import '../../../../styles/reskinned-task-manager.css';
import {
  STATUS,
  TASK_TYPE,
} from '../ImprovedTaskManager/useImprovedTaskManager';
import styles from './taskForm.module.css';
import { Box } from '@chakra-ui/react';
import size from 'lodash/size';

/**
 * ImprovedTaskForm component for adding and editing tasks
 * Uses react-hook-form for form management
 *
 * Parent
 *      ImprovedTaskManager
 */
function ImprovedTaskForm({
  isOpen,
  onClose,
  onSuccess,
  selectedTask = null,
  users = [],
}) {
  const {
    // Form state
    handleSubmit,
    isSubmitting,
    isFormValid,
    control,
    userOptions,

    // Modal state
    isEditMode,

    // Functions
    resetForm,
    handleClose,
  } = useImprovedTaskForm({
    isOpen,
    onClose,
    onSuccess,
    selectedTask,
    users,
  });

  // Reset form when modal opens or selectedTask changes
  useEffect(() => {
    let timeout = null;
    if (isOpen) {
      timeout = resetForm();
    }
    return () => {
      if (timeout) {
        clearTimeout(timeout);
      }
    };
  }, [isOpen, selectedTask, resetForm]);

  const modalTitle = isEditMode ? 'Edit Task' : 'Add Task';

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={modalTitle}
      className="nexus-task-modal"
    >
      <form onSubmit={handleSubmit}>
        <Box p={6}>
          <div className="nexus-form-row">
            <div className="nexus-form-group">
              <Controller
                name="taskName"
                control={control}
                rules={{
                  required: 'Task name is required',
                  minLength: {
                    value: 2,
                    message: 'Task name must be at least 2 characters',
                  },
                }}
                defaultValue=""
                render={({ field, fieldState }) => (
                  <TextInput
                    id="taskName"
                    label="Task name"
                    type="text"
                    placeholder="Task name"
                    value={field.value}
                    onChange={field.onChange}
                    errorMessage={fieldState.error?.message}
                    clearable
                  />
                )}
              />
            </div>
            <div className="nexus-form-group">
              <Controller
                name="taskType"
                control={control}
                rules={{ required: 'Please select a task type' }}
                defaultValue=""
                render={({ field, fieldState }) => {
                  // task type will be disabled in new version; user is not allowed to change task type
                  return (
                    <SelectMenu
                      id="taskType"
                      label="Task Type"
                      options={TASK_TYPE}
                      value={field.value}
                      onChange={(_, value) => field.onChange(value)}
                      error={fieldState.error}
                      placeholder="Select task type"
                      disabled
                    />
                  );
                }}
              />
            </div>
          </div>

          <div className="nexus-form-row">
            <div className="nexus-form-group">
              <Controller
                name="priority"
                control={control}
                rules={{ required: 'Priority is required' }}
                defaultValue=""
                render={({ field, fieldState }) => (
                  <SelectMenu
                    options={[
                      { value: 'high', label: 'High' },
                      { value: 'medium', label: 'Medium' },
                      { value: 'low', label: 'Low' },
                    ]}
                    label="Priority"
                    placeholder="Select priority"
                    value={field.value}
                    onChange={(event, value) => {
                      field.onChange(value);
                    }}
                    error={fieldState.error}
                    disabled={selectedTask?.version === '1'}
                  />
                )}
              />
            </div>
            {!!size(selectedTask?.location_label) && (
              <div className="nexus-form-group">
                <SelectMenu
                  id="location"
                  label="Location"
                  options={[
                    {
                      value: selectedTask?.location_label,
                      label: selectedTask?.location_label,
                    },
                  ]}
                  value={selectedTask?.location_label}
                  onChange={() => {}}
                  disabled
                />
              </div>
            )}

            <div className="nexus-form-group">
              <Controller
                name="status"
                control={control}
                rules={{ required: 'Status is required' }}
                defaultValue=""
                render={({ field, fieldState }) => (
                  <SelectMenu
                    label="Status"
                    options={STATUS}
                    value={field.value}
                    onChange={(_, value) => field.onChange(value)}
                    error={fieldState.error}
                    placeholder="Select status"
                  />
                )}
              />
            </div>
          </div>

          {/* Priority - Available for both add and edit modes */}
          <div className="nexus-form-row">
            <div className="nexus-form-group">
              <Controller
                name="ownerId"
                control={control}
                rules={{ required: 'Please select an assignee' }}
                defaultValue=""
                render={({ field, fieldState }) => (
                  <SelectMenu
                    id="ownerId"
                    label="Assigned To"
                    options={userOptions}
                    value={field.value}
                    onChange={(_, value) => field.onChange(value)}
                    error={fieldState.error}
                    placeholder="Select assignee"
                    className={styles.taskFormField}
                  />
                )}
              />
            </div>
            <div className="nexus-form-group">
              <Controller
                name="dueDate"
                control={control}
                rules={{
                  required: 'Due date is required',
                }}
                defaultValue=""
                render={({ field, fieldState }) => {
                  return (
                    <DateInput
                      id="dueDate"
                      label="Due Date"
                      value={field.value}
                      onChange={field.onChange}
                      error={fieldState.error}
                      placeholder="MM/DD/YYYY"
                    />
                  );
                }}
              />
            </div>
          </div>

          {/* Status field is now always available above */}

          <div className="nexus-form-group">
            <Controller
              name="comment"
              control={control}
              defaultValue=""
              render={({ field, fieldState }) => (
                <TextArea
                  id="comment"
                  label="Comments"
                  placeholder="Write your comments..."
                  rows={4}
                  value={field.value}
                  onChange={field.onChange}
                  errorMessage={fieldState.error?.message}
                />
              )}
            />
          </div>
        </Box>

        <ModalFooter>
          <Button variant="tonal" onClick={handleClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button type="submit" disabled={isSubmitting || !isFormValid}>
            {isSubmitting ? 'Saving...' : 'Save'}
          </Button>
        </ModalFooter>
      </form>
    </Modal>
  );
}

export default memo(ImprovedTaskForm);
